import { Injectable } from '@angular/core';
import { Benefit } from '@models/Sessions/Idea/Benefit';
import { Challenge } from '@models/Sessions/Idea/Challenge';
import {
  CollaborationInvite,
  Collaborator,
} from '@models/Sessions/Idea/Collaborator';
import { Comment } from '@models/Sessions/Idea/Comment';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Solution } from '@models/Sessions/Idea/Solution';
import { Vote } from '@models/Sessions/Idea/Vote';
import { Session } from '@models/Sessions/Session';

import { IdeaFeedback } from '../../models/Sessions/Idea/feedback';
import { BusService } from '../bus.service';
import { EventsService } from '../events.service';
import { RequestService } from '../request.service';

export interface IIdeaGetResponse {
  idea: Idea;
  session: Session;
}

@Injectable()
export class IdeaRequestService {
  api = {
    root: '/session',
    idea: '/ideas',
    follow: '/follow',
    unfollow: '/unfollow',
    vote: '/votes',
    comment: '/comments',
    challenge: '/challenges',
    benefit: '/benefits',
    collaborator: '/collaborators',
    invites: '/invites',
    requestCollaboration: '/request-collaboration',
    approveCollaboration: '/approve-collaboration',
    publish: '/publish',
    unpublish: '/unpublish',
    solution: '/solutions',
    acceptSolution: '/accept',
    unacceptSolution: '/unaccept',
    mature: '/mature',
    editable: '/editable',
    lock: '/lock',
    position: '/position',
    backlink: '/backlink',
  };

  constructor(
    private client: RequestService,
    private bus: BusService,
    private events: EventsService,
  ) {
    this.subscribe();
  }

  getSingle(ideaId: string, sessionId: string) {
    return this.client.get<IIdeaGetResponse>({
      uri: this.api.root + `/${sessionId}` + this.api.idea + `/${ideaId}`,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.data.session.idea.single.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.data.session.idea.single.failure,
            error.error,
          ),
      },
    });
  }

  getWithSession(ideaId: string, sessionId: string, notification?: any) {
    const sessionPromise = this.client
      .get<any>({ uri: this.api.root + `/${sessionId}` })
      .toPromise();
    const ideaPromise = this.client
      .get<any>({
        uri: this.api.root + `/${sessionId}` + this.api.idea + `/${ideaId}`,
      })
      .toPromise();

    Promise.all([sessionPromise, ideaPromise])
      .then(([sessionResponse, ideaResponse]) => {
        if (sessionResponse && ideaResponse) {
          this.bus.publish(
            this.events.received.data.session.idea.singleWithSession.success,
            { session: sessionResponse, idea: ideaResponse.idea, notification },
          );
        }
      })
      .catch(error => {
        error.notification = notification;
        this.bus.publish(
          this.events.received.data.session.idea.singleWithSession.failure,
          error,
        );
      });
  }

  isEditable(idea: Idea, session: Session) {
    this.client.get<IIdeaGetResponse>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.editable,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.data.session.idea.editable.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.data.session.idea.editable.failure,
            error.error,
          ),
      },
    });
  }

  setLockStatus(status: boolean, idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.lock,
      body: { lock: status },
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.lock.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.lock.failure,
            error.error,
          ),
      },
    });
  }

  update(idea: Idea, session: Session) {
    this.client.put<any, any>({
      uri: this.api.root + `/${session._id}` + this.api.idea + `/${idea._id}`,
      body: idea,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.update.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.update.failure,
            error.error,
          ),
      },
    });
  }

  create(idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri: this.api.root + `/${session._id}` + this.api.idea,
      body: idea,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.create.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.create.failure,
            error.error,
          ),
      },
    });
  }

  follow(idea: Idea, session: Session) {
    return this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.follow,
      body: {},
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.follow.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.follow.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  unfollow(idea: Idea, session: Session) {
    return this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.unfollow,
      body: {},
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.unfollow.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.unfollow.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  vote(vote: Vote, idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.vote,
      body: vote,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.vote.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.vote.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  addComment(comment: Comment, idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.comment,
      body: comment,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.addComment.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.addComment.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  deleteComment(comment: Comment, idea: Idea, session: Session) {
    this.client.delete<any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.comment +
        `/${comment._id}`,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.deleteComment.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.deleteComment.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  addChallenge(challenge: Challenge, idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.challenge,
      body: challenge,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.add.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.add.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  deleteChallenge(challenge: Challenge, idea: Idea, session: Session) {
    this.client.delete<any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.challenge +
        `/${challenge._id}`,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.delete.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.delete.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  addBenefit(benefit: Benefit, idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.benefit,
      body: benefit,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.addBenefit.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.addBenefit.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  deleteBenefit(benefit: Benefit, idea: Idea, session: Session) {
    this.client.delete<any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.benefit +
        `/${benefit._id}`,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.deleteBenefit.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.deleteBenefit.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  addCollaborator(collaborator: Collaborator, idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.collaborator,
      body: collaborator,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.addCollaborator.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.addCollaborator.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  addCollaborationRequest(
    participant_id: string,
    idea: Idea,
    session: Session,
  ) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.requestCollaboration,
      body: participant_id,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.requestCollaboration
              .success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.requestCollaboration
              .failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  approveCollaboration(participant_id: string, idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.approveCollaboration,
      body: participant_id,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.approveCollaboration
              .success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.approveCollaboration
              .failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  deleteCollaborator(collaborator: Collaborator, idea: Idea, session: Session) {
    this.client.delete<any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.collaborator +
        `/${collaborator._id}`,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.deleteCollaborator.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.deleteCollaborator.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  deleteCollaborationInvite(
    participantId: string,
    idea: Idea,
    session: Session,
  ) {
    return this.client.delete<any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.collaborator +
        this.api.invites +
        `/${participantId}`,
      handlers: {
        success: () => void 0,
        error: () => void 0,
      },
    });
  }

  publish(idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.publish,
      body: {},
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.publish.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.publish.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  unpublish(idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.unpublish,
      body: {},
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.unpublish.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.unpublish.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  setMature(isMature: boolean, idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.mature,
      body: { mature: isMature },
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.setMature.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.setMature.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  backlink(idea: Idea, session: Session) {
    return this.client.get<{ session?: Session; idea?: Idea }>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.backlink,
      handlers: {
        success: () => void 0,
        error: () => void 0,
      },
    });
  }

  delete(idea: Idea, session: Session) {
    this.client.delete<any>({
      uri: this.api.root + `/${session._id}` + this.api.idea + `/${idea._id}`,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.delete.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.delete.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  savePosition(idea: Idea, session: Session) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.position,
      body: { position: idea.position },
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.savePosition.success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.savePosition.failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  // Solutions

  proposeSolution(
    solution: Solution,
    challenge: Challenge,
    idea: Idea,
    session: Session,
  ) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.challenge +
        `/${challenge._id}` +
        this.api.solution,
      body: solution,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.solution.propose
              .success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.solution.propose
              .failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  acceptSolution(
    solution: Solution,
    challenge: Challenge,
    idea: Idea,
    session: Session,
  ) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.challenge +
        `/${challenge._id}` +
        this.api.solution +
        `/${solution._id}` +
        this.api.acceptSolution,
      body: {},
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.solution.accept
              .success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.solution.accept
              .failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  unacceptSolution(
    solution: Solution,
    challenge: Challenge,
    idea: Idea,
    session: Session,
  ) {
    this.client.post<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.challenge +
        `/${challenge._id}` +
        this.api.solution +
        `/${solution._id}` +
        this.api.unacceptSolution,
      body: {},
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.solution.unaccept
              .success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.solution.unaccept
              .failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  deleteSolution(
    solution: Solution,
    challenge: Challenge,
    idea: Idea,
    session: Session,
  ) {
    this.client.delete<any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.challenge +
        `/${challenge._id}` +
        this.api.solution +
        `/${solution._id}`,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.solution.delete
              .success,
            { response, idea },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.idea.challenge.solution.delete
              .failure,
            { error: error.error, idea },
          ),
      },
    });
  }

  acceptCollaborationInvite(
    session: Session,
    idea: Idea,
    invite: CollaborationInvite,
  ) {
    return this.client.put<{ status: string }, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.collaborator +
        this.api.invites +
        `/${invite.participant_id}`,
      body: {
        status: 'accepted',
      },
      handlers: {
        success: () => void 0,
        error: () => void 0,
      },
    });
  }

  rejectCollaborationInvite(
    session: Session,
    idea: Idea,
    invite: CollaborationInvite,
  ) {
    return this.client.put<{ status: string }, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        this.api.collaborator +
        this.api.invites +
        `/${invite.participant_id}`,
      body: {
        status: 'rejected',
      },
      handlers: {
        success: () => void 0,
        error: () => void 0,
      },
    });
  }

  postFeedback(session: Session, idea: Idea, feedback: IdeaFeedback) {
    return this.client.post<IdeaFeedback, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.idea +
        `/${idea._id}` +
        '/feedbacks',
      body: feedback,
      handlers: {
        success: () => void 0,
        error: () => void 0,
      },
    });
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.requested.data.session.idea.single,
      this.getSingle.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.data.session.idea.singleWithSession,
      this.getWithSession.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.data.session.idea.editable,
      this.isEditable.bind(this),
    );

    this.bus.subscribe(
      this.events.requested.action.session.idea.lock,
      this.setLockStatus.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.update,
      this.update.bind(this),
    );

    this.bus.subscribe(
      this.events.requested.action.session.idea.follow,
      this.follow.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.unfollow,
      this.unfollow.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.create,
      this.create.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.vote,
      this.vote.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.addComment,
      this.addComment.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.deleteComment,
      this.deleteComment.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.challenge.add,
      this.addChallenge.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.challenge.delete,
      this.deleteChallenge.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.addBenefit,
      this.addBenefit.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.deleteBenefit,
      this.deleteBenefit.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.addCollaborator,
      this.addCollaborator.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.collaborationRequested,
      this.addCollaborationRequest.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.collaborationApproved,
      this.approveCollaboration.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.deleteCollaborator,
      this.deleteCollaborator.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.publish,
      this.publish.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.unpublish,
      this.unpublish.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.setMature,
      this.setMature.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.delete,
      this.delete.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.savePosition,
      this.savePosition.bind(this),
    );
    // Solutions
    this.bus.subscribe(
      this.events.requested.action.session.idea.challenge.solution.propose,
      this.proposeSolution.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.challenge.solution.accept,
      this.acceptSolution.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.challenge.solution.unaccept,
      this.unacceptSolution.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.challenge.solution.delete,
      this.deleteSolution.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.idea.feedback.post,
      this.postFeedback.bind(this),
    );
  }
}
